import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTourStore } from '@/stores/tour';
import { usePermissions } from '@/hooks/usePermissions';
import type { TourPlacement, TourStep } from '@/lib/tour/types';
import './TourOverlay.css';

type Rect = { top: number; left: number; width: number; height: number };

const PADDING = 8;
const TOOLTIP_GAP = 12;
const POLL_INTERVAL = 100;
const POLL_TIMEOUT = 3000;

function getTargetRect(selector: string): Rect | null {
  const el = document.querySelector(selector);
  if (!el) {
    return null;
  }
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function scrollTargetIntoView(selector: string) {
  const el = document.querySelector(selector);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }
}

function computeTooltipStyle(
  rect: Rect,
  placement: TourPlacement,
  tooltipRef: React.RefObject<HTMLDivElement | null>,
): React.CSSProperties {
  const tooltip = tooltipRef.current;
  const tw = tooltip?.offsetWidth ?? 320;
  const th = tooltip?.offsetHeight ?? 200;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top = 0;
  let left = 0;
  let actualPlacement = placement;

  // Check if preferred placement fits, otherwise flip
  if (placement === 'bottom' && rect.top + rect.height + TOOLTIP_GAP + th > vh) {
    actualPlacement = 'top';
  } else if (placement === 'top' && rect.top - TOOLTIP_GAP - th < 0) {
    actualPlacement = 'bottom';
  } else if (placement === 'right' && rect.left + rect.width + TOOLTIP_GAP + tw > vw) {
    actualPlacement = 'left';
  } else if (placement === 'left' && rect.left - TOOLTIP_GAP - tw < 0) {
    actualPlacement = 'right';
  }

  switch (actualPlacement) {
    case 'bottom':
      top = rect.top + rect.height + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'top':
      top = rect.top - TOOLTIP_GAP - th;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left + rect.width + TOOLTIP_GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - th / 2;
      left = rect.left - TOOLTIP_GAP - tw;
      break;
  }

  // Clamp to viewport
  left = Math.max(12, Math.min(left, vw - tw - 12));
  top = Math.max(12, Math.min(top, vh - th - 12));

  return { top: `${top}px`, left: `${left}px` };
}

export function TourOverlay() {
  const { activeTour, nextStep, prevStep, dismissTour } = useTourStore();
  const { role } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [waiting, setWaiting] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Steps and current step come from activeTour directly (already filtered)
  const steps: TourStep[] = activeTour?.steps ?? [];
  const totalSteps = steps.length;
  const currentStepIndex = activeTour?.currentStep ?? 0;
  const step: TourStep | undefined = steps[currentStepIndex];

  // Resolve role-specific content, falling back to the step's defaults
  const roleVariant = role ? step?.roleContent?.[role] : undefined;
  const displayTitle = roleVariant?.title ?? step?.title;
  const displayContent = roleVariant?.content ?? step?.content;

  // Navigate to step's route if needed
  useEffect(() => {
    if (!step?.navigateTo) {
      return;
    }
    if (location.pathname !== step.navigateTo) {
      void navigate(step.navigateTo);
    }
  }, [step?.navigateTo, location.pathname, navigate]);

  // Find and track the target element
  // Poll for target element (handles navigation + lazy rendering)
  useEffect(() => {
    if (!step) {
      return;
    }

    const rect = getTargetRect(step.target);
    if (rect) {
      setTargetRect(rect);
      scrollTargetIntoView(step.target);
      setWaiting(false);
      step.onActivate?.();
      return;
    }

    // Target not found yet — poll for it
    if (step.waitForTarget || step.navigateTo) {
      setWaiting(true);
      const start = Date.now();
      const interval = setInterval(() => {
        const r = getTargetRect(step.target);
        if (r) {
          setTargetRect(r);
          scrollTargetIntoView(step.target);
          setWaiting(false);
          step.onActivate?.();
          clearInterval(interval);
        } else if (Date.now() - start > POLL_TIMEOUT) {
          // Target never appeared — show tooltip centered without spotlight
          setTargetRect(null);
          setWaiting(false);
          clearInterval(interval);
        }
      }, POLL_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [step, location.pathname]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!step || !targetRect) {
      return;
    }
    const update = () => {
      const r = getTargetRect(step.target);
      if (r) {
        setTargetRect(r);
      }
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step, targetRect]);

  // Keyboard navigation
  useEffect(() => {
    if (!activeTour) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismissTour();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        prevStep();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeTour, nextStep, prevStep, dismissTour]);

  if (!activeTour || totalSteps === 0 || !step) {
    return null;
  }
  if (waiting) {
    return (
      <div className="tour-overlay">
        <div className="tour-backdrop" />
        <div className="tour-loading">
          <div className="tour-loading-spinner" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  const placement = step.placement ?? 'bottom';
  const tooltipStyle = targetRect
    ? computeTooltipStyle(targetRect, placement, tooltipRef)
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="tour-overlay">
      {/* Backdrop with cutout */}
      <svg className="tour-backdrop-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - PADDING}
                y={targetRect.top - PADDING}
                width={targetRect.width + PADDING * 2}
                height={targetRect.height + PADDING * 2}
                rx="8"
                ry="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.6)" mask="url(#tour-mask)" />
      </svg>

      {/* Spotlight ring around target */}
      {targetRect && (
        <div
          className="tour-spotlight"
          style={{
            top: `${targetRect.top - PADDING}px`,
            left: `${targetRect.left - PADDING}px`,
            width: `${targetRect.width + PADDING * 2}px`,
            height: `${targetRect.height + PADDING * 2}px`,
          }}
        />
      )}

      {/* Tooltip */}
      <div className="tour-tooltip" ref={tooltipRef} style={tooltipStyle} role="dialog" aria-label={displayTitle}>
        <div className="tour-tooltip-header">
          <h3 className="tour-tooltip-title">{displayTitle}</h3>
          <button className="tour-tooltip-close" onClick={dismissTour} aria-label="Close tour">
            <X size={14} />
          </button>
        </div>
        <p className="tour-tooltip-content">{displayContent}</p>
        <div className="tour-tooltip-footer">
          <div className="tour-tooltip-progress">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`tour-dot${i === currentStepIndex ? ' active' : i < currentStepIndex ? ' completed' : ''}`}
              />
            ))}
          </div>
          <div className="tour-tooltip-actions">
            {currentStepIndex > 0 && (
              <button className="tour-btn tour-btn-secondary" onClick={prevStep}>
                <ChevronLeft size={14} />
                Back
              </button>
            )}
            {currentStepIndex === 0 && (
              <button className="tour-btn tour-btn-ghost" onClick={dismissTour}>
                Skip
              </button>
            )}
            <button className="tour-btn tour-btn-primary" onClick={nextStep}>
              {currentStepIndex === totalSteps - 1 ? 'Done' : 'Next'}
              {currentStepIndex < totalSteps - 1 && <ChevronRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
